import React, { useRef } from 'react';
import { useApp } from '../contexts/AppContext';
import StampGrid from './StampGrid';

const DAYS_EN = [
  {
    num: 1,
    theme: 'What Is AI?',
    themeJa: 'AIとは何か？',
    color: '#118AB2',
    emoji: '🤖',
    morning: 'Wake Up Round: What is a robot / computer / AI? · Flashcard Round (12 cards) · Build the AI Detector Box',
    afternoon: 'AI Treasure Hunt (6 envelopes) · Draw It Round · Quiz Competition (15 questions)',
    vocab: ['AI', 'Artificial Intelligence', 'Data', 'Patterns', 'Predictions'],
    vocabJa: ['AI', '人工知能', 'データ', 'パターン', '予測'],
    keyLesson: 'AI learns from DATA · AI finds PATTERNS — not meaning · AI is a TOOL — humans are RESPONSIBLE',
    keyLessonJa: 'AIはデータから学ぶ・AIはパターンを見つける（意味ではない）・AIはツール——人間が責任を持つ',
    activity: 'Build AI Detector Box · AI or NOT AI? Picture Cards Sort',
    activityJa: 'AI検出機ボックス作り・「AIか？AIじゃないか？」カード分類',
  },
  {
    num: 2,
    theme: 'How AI Learns',
    themeJa: 'AIの学び方',
    color: '#06D6A0',
    emoji: '🧠',
    morning: 'Day 1 Review · Data Sort Game: "Learns or Doesn\'t Learn?" (2 boxes) · Human Computer Game — students become the AI',
    afternoon: 'Pattern Race (10 rounds) · Draw It Round · Quiz Competition (15 questions)',
    vocab: ['Training Data', 'Machine Learning', 'Label', 'Accurate', 'Prediction'],
    vocabJa: ['訓練データ', '機械学習', 'ラベル', '正確さ', '予測'],
    keyLesson: 'AI learns from DATA · AI finds PATTERNS in data · More data = MORE ACCURATE AI',
    keyLessonJa: 'AIはデータから学ぶ・データのパターンを見つける・データが多い＝より正確なAI',
    activity: '"Learns or Doesn\'t Learn?" Card Sort · Pattern Race · Human Computer Role Play',
    activityJa: '「学ぶ・学ばない」カード分類・パターンレース・人間コンピューターゲーム',
  },
  {
    num: 3,
    theme: 'AI Gets It Wrong',
    themeJa: 'AIの間違い',
    color: '#EF4444',
    emoji: '⚠️',
    morning: 'Days 1–2 Review · AI Mistake Cards: 4 categories · Red Team Challenge (find 3 weaknesses in an AI system)',
    afternoon: 'Fix the AI Game (5 scenarios) · Draw It Round · Quiz Competition (15 questions)',
    vocab: ['Bias', 'Hallucination', 'Edge Case', 'Bad Data', 'Red Teaming'],
    vocabJa: ['バイアス', 'ハルシネーション', 'エッジケース', '悪いデータ', 'レッドチーム'],
    keyLesson: '4 mistake types: BIAS · HALLUCINATION · EDGE CASE · BAD DATA · Fix = Better Data + Human Oversight',
    keyLessonJa: '4種類のミス：バイアス・ハルシネーション・エッジケース・悪いデータ・修正＝より良いデータ＋人間の監視',
    activity: 'AI Mistake Card Sort · Red Team Challenge Sheet · Fix the AI Scenarios',
    activityJa: 'AIミスカード分類・レッドチームチャレンジ・AIを修正するシナリオ',
  },
  {
    num: 4,
    theme: 'AI in My World',
    themeJa: '生活の中のAI',
    color: '#A855F7',
    emoji: '🌍',
    morning: 'Days 1–3 Review · AI Hunt (8 scene cards: Phone/Hospital/Car/School/Mall/Airport/Home/Farm) · Good AI vs Bad AI — Take a Side',
    afternoon: 'AI Design Challenge (20 min + 30-sec pitch) · Draw It Round · Quiz Competition (15 questions)',
    vocab: ['Computer Vision', 'NLP', 'Recommendation', 'AI Ethics', 'AI Literacy'],
    vocabJa: ['コンピュータビジョン', '自然言語処理', 'レコメンデーション', 'AI倫理', 'AIリテラシー'],
    keyLesson: 'AI is everywhere NOW · Good AI = Helps people + Is fair + Has human oversight · AI is a TOOL — humans are RESPONSIBLE',
    keyLessonJa: 'AIは今すでに身の回りにある・良いAI＝人を助ける＋公平＋人間の監視あり・AIはツール——人間が責任を持つ',
    activity: 'AI Hunt (8 scenes) · Good AI vs Bad AI debate · AI Design Challenge pitch',
    activityJa: 'AIハント（8シーン）・「良いAI vs 悪いAI」ディベート・AIデザインチャレンジ発表',
  },
  {
    num: 5,
    theme: 'Graduation Day!',
    themeJa: '卒業の日！',
    color: '#F59E0B',
    emoji: '🎓',
    morning: '5-Day Review Drill · Big Quiz (20 questions, 4 rounds) · AI Detector Final Test (12 cards A–L)',
    afternoon: 'Fun AI Games (Song Maker · Voice Telephone · Face Fooler · Game Designer) · Bootcamp Showcase · Graduation Ceremony',
    vocab: ['Responsible', 'Diverse', 'Oversight', 'Fair', 'Design'],
    vocabJa: ['責任ある', '多様な', '監視', '公平', 'デザイン'],
    keyLesson: '"I am not afraid of AI because I UNDERSTAND it!" · AI Literacy — COMPLETE!',
    keyLessonJa: '「AIを理解しているから怖くない！」・AIリテラシー——コンプリート！',
    activity: 'Bootcamp Showcase · Graduation Ceremony · One Word Memory · Group Celebration',
    activityJa: 'ブートキャンプ発表会・卒業式・ワンワードメモリー・グループお祝い',
  },
];

export default function AILiteracyBooklet({ student, stamps, activities, programId }) {
  const { t, lang } = useApp();
  const ref = useRef();

  const handlePrint = () => window.print();

  const displayName = lang === 'ja' ? (student.name_ja || student.name) : student.name;

  const getStampsForDay = (day) =>
    stamps?.filter(s => s.program_id === programId && s.day_number === day) || [];

  const getActivity = (day, key) =>
    activities?.find(a => a.program_id === programId && a.day_number === day && a.activity_key === key);

  return (
    <div>
      {/* Print button */}
      <div className="d-flex justify-content-between align-items-center mb-3 no-print">
        <div style={{ fontSize: '0.82rem', color: 'var(--muted)', fontWeight: 600 }}>
          💡 {lang === 'ja'
            ? 'ブラウザの印刷ダイアログで「用紙サイズ: A4」「余白: デフォルト」を選んでください'
            : 'In the print dialog: Paper size A4 · Margins Default · Color'}
        </div>
        <button className="btn-summer" onClick={handlePrint} style={{ flexShrink: 0, padding: '0.5rem 1.5rem' }}>
          🖨️ {lang === 'ja' ? 'ブックレットを印刷' : 'Print Booklet'}
        </button>
      </div>

      <div ref={ref} className="booklet-print-wrapper">
        {/* Fixed footer — repeats on every printed page */}
        <div className="booklet-print-footer">
          <span className="footer-brand">🦉 Ryukyu GLOCAL Kids AI Literacy Bootcamp 2026</span>
          <span>{displayName}</span>
          <span className="footer-page">{lang === 'ja' ? 'ページ ' : 'Page '}</span>
        </div>

        {/* ── COVER PAGE ── */}
        <div
          className="booklet-page booklet-cover mb-4"
          style={{
            background: 'linear-gradient(160deg, #073B4C 0%, #118AB2 45%, #06D6A0 100%)',
            color: 'white',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div className="text-center" style={{ padding: '2rem' }}>
            {/* Circular logo */}
            <div style={{
              width: 140, height: 140, borderRadius: '50%',
              background: 'white',
              border: '4px solid rgba(255,255,255,0.7)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1.8rem',
              overflow: 'hidden',
            }}>
              <img src="/logo.png" alt="Ryukyu GLOCAL Kids" style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
            </div>

            <div style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.25em', opacity: 0.8, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
              Ryukyu GLOCAL Kids
            </div>

            <h2 style={{ fontWeight: 900, fontSize: '2.2rem', marginBottom: '0.4rem', lineHeight: 1.2 }}>
              {lang === 'ja' ? 'AIリテラシー' : 'AI Literacy'}
            </h2>
            <div style={{ fontSize: '1.1rem', opacity: 0.9, fontWeight: 700, marginBottom: '0.5rem' }}>
              {lang === 'ja' ? 'ブートキャンプ 2026 · 沖縄' : 'Bootcamp 2026 · Okinawa'}
            </div>

            <div style={{ fontSize: '2.2rem', letterSpacing: '0.6rem', marginBottom: '2.5rem' }}>🤖🧠⚠️🌍🎓</div>

            {/* Student name card */}
            <div style={{
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(6px)',
              borderRadius: 20,
              padding: '1.2rem 2.5rem',
              display: 'inline-block',
              minWidth: 240,
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}>
              <div style={{ fontSize: '0.72rem', opacity: 0.8, fontWeight: 700, marginBottom: '0.4rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                {lang === 'ja' ? '生徒名' : 'Student Name'}
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 900 }}>{displayName}</div>
            </div>

            <div style={{ marginTop: '2rem', opacity: 0.7, fontSize: '0.8rem', fontWeight: 600 }}>
              {lang === 'ja' ? 'AIリテラシー・アクティビティブックレット' : 'AI Literacy Activity Booklet'}
            </div>
          </div>
        </div>

        {/* ── STAMP OVERVIEW ── */}
        <div className="booklet-page booklet-stamps">
          <div className="booklet-day-header">
            <span style={{ fontSize: '1.5rem' }}>⭐</span>
            <div>
              <h5 style={{ fontWeight: 900, margin: 0 }}>{t.stamps}</h5>
              <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{t.weekProgress}</div>
            </div>
          </div>
          <StampGrid stamps={stamps || []} programId={programId} programSlug="ai-literacy" />
        </div>

        {/* ── DAY PAGES ── */}
        {DAYS_EN.map((day) => {
          const dayStamps = getStampsForDay(day.num);
          const ideaActivity = getActivity(day.num, 'ai_reflection');
          const hasContent = ideaActivity?.content;

          const theme = lang === 'ja' ? day.themeJa : day.theme;
          const morning = day.morning;
          const afternoon = day.afternoon;
          const vocab = lang === 'ja' ? day.vocabJa : day.vocab;
          const keyLesson = lang === 'ja' ? day.keyLessonJa : day.keyLesson;
          const activity = lang === 'ja' ? day.activityJa : day.activity;

          return (
            <div key={day.num} className="booklet-page" style={{ borderTopColor: day.color }}>
              {/* Day header */}
              <div className="booklet-day-header">
                <div>
                  <div className="day-badge" style={{ background: `linear-gradient(135deg, ${day.color}, ${day.color}99)` }}>
                    {lang === 'ja' ? `${day.num}日目` : `Day ${day.num}`} {day.emoji}
                  </div>
                </div>
                <div>
                  <h5 style={{ fontWeight: 900, margin: 0, color: day.color }}>{theme}</h5>
                </div>
                {/* Stamp mini circles */}
                <div className="ms-auto d-flex gap-1">
                  {['morning', 'afternoon'].map(s => {
                    const earned = dayStamps.find(st => st.session === s);
                    return (
                      <div key={s} style={{
                        width: 36, height: 36, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1.1rem',
                        background: earned ? `linear-gradient(135deg, ${day.color}, #06D6A0)` : '#F3F4F6',
                        boxShadow: earned ? `0 4px 12px ${day.color}44` : 'none',
                      }}>
                        {earned ? '⭐' : ''}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Key vocabulary */}
              <div className="activity-section mb-3" style={{ borderLeftColor: day.color }}>
                <h6 style={{ color: day.color }}>{lang === 'ja' ? '重要ワード' : 'Key Vocabulary'}</h6>
                <div className="d-flex flex-wrap gap-2">
                  {vocab.map(v => (
                    <span key={v} style={{
                      background: `${day.color}18`, color: day.color,
                      borderRadius: 8, padding: '3px 10px',
                      fontSize: '0.8rem', fontWeight: 700,
                    }}>
                      {v}
                    </span>
                  ))}
                </div>
              </div>

              {/* Sessions */}
              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <div className="activity-section h-100" style={{ borderLeftColor: '#06D6A0' }}>
                    <h6 style={{ color: '#059669' }}>☀️ {lang === 'ja' ? '午前' : 'Morning'}</h6>
                    <div style={{ fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.6 }}>{morning}</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="activity-section h-100" style={{ borderLeftColor: day.color }}>
                    <h6 style={{ color: day.color }}>🌙 {lang === 'ja' ? '午後' : 'Afternoon'}</h6>
                    <div style={{ fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.6 }}>{afternoon}</div>
                  </div>
                </div>
              </div>

              {/* Key lesson */}
              <div className="activity-section mb-3" style={{ borderLeftColor: day.color, background: `${day.color}08` }}>
                <h6 style={{ color: day.color }}>💡 {lang === 'ja' ? '今日の重要ポイント' : 'Key Lesson'}</h6>
                <div style={{ fontSize: '0.8rem', color: 'var(--text)', fontWeight: 600, lineHeight: 1.6 }}>{keyLesson}</div>
              </div>

              {/* Activity / Reflection space */}
              <div className="activity-section" style={{ borderLeftColor: '#E5E7EB', background: '#FAFAFA' }}>
                <h6 style={{ color: 'var(--muted)' }}>📝 {lang === 'ja' ? 'アクティビティ' : 'Activity'}</h6>
                {hasContent ? (
                  <div style={{ fontSize: '0.85rem', lineHeight: 1.7 }}>
                    {Object.entries(hasContent).map(([k, v]) => v && (
                      <div key={k} className="mb-1">
                        <span style={{ fontWeight: 700, color: day.color }}>{k.replace(/_/g, ' ')}: </span>
                        <span>{v}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8rem', color: '#D1D5DB', fontStyle: 'italic' }}>{activity}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
